import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { tableAPI, recordAPI, permissionsAPI, authAPI, mgbSetupAPI } from '../api/api';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import './TableViewer.css';

function TableForm() {
  const { tableName, recordId } = useParams();
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({});
  const [foreignKeyData, setForeignKeyData] = useState({});
  const [foreignKeys, setForeignKeys] = useState([]);
  const [linkedFieldOptions, setLinkedFieldOptions] = useState({});
  const [fieldLinks, setFieldLinks] = useState([]);
  const [primaryKeyFields, setPrimaryKeyFields] = useState([]);
  const [format, setFormat] = useState(null);
  const [crossTableConfigs, setCrossTableConfigs] = useState({});
  const [crossTableAvailableItems, setCrossTableAvailableItems] = useState({});
  const [crossTableSelectedItems, setCrossTableSelectedItems] = useState({});
  const [permissions, setPermissions] = useState({
    CanView: true,
    CanCreate: false,
    CanEdit: false
  });
  const [isAdmin, setIsAdmin] = useState(false);

  const isEditMode = !!recordId;

  useEffect(() => {
    if (tableName) {
      fetchTableStructure();
      checkPermissions();
    }
  }, [tableName, recordId]);

  useEffect(() => {
    if (foreignKeyData && Object.keys(foreignKeyData).length > 0 && fieldLinks.length > 0) {
      loadLinkedFieldOptions();
    }
  }, [foreignKeyData, fieldLinks]);

  useEffect(() => {
    // Load options for dropdown fields that have SQLSource directly (not in fieldLinks)
    // This runs after fields and fieldLinks are set
    if (fields.length > 0) {
      // Use a small delay to ensure fieldLinks have been processed
      const timer = setTimeout(() => {
        loadDropdownFieldOptions();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [fields, fieldLinks]);

  useEffect(() => {
    // When linkedFieldOptions are loaded and we're in edit mode, reprocess the record
    if (isEditMode && recordId && linkedFieldOptions && Object.keys(linkedFieldOptions).length > 0 && formData && Object.keys(formData).length > 0) {
      const processedRecord = { ...formData };
      for (const link of fieldLinks) {
        if (link.IsLinked === 1 || link.IsLinked === true) {
          const fieldValue = processedRecord[link.FieldName];
          if (fieldValue && linkedFieldOptions[link.FieldName]) {
            const matchedOption = linkedFieldOptions[link.FieldName].find(
              opt => String(opt.display) === String(fieldValue)
            );
            if (matchedOption) {
              processedRecord[link.FieldName] = String(matchedOption.id);
            }
          }
        }
      }
      // Preserve date fields when updating formData
      setFormData(processedRecord);
    }
  }, [linkedFieldOptions, isEditMode, recordId]);

  // Ensure date fields are formatted after formData is set
  useEffect(() => {
    if (isEditMode && formData && Object.keys(formData).length > 0 && fields.length > 0) {
      const updated = { ...formData };
      let changed = false;
      
      for (const field of fields) {
        const fieldDataType = (field.datatype || field.dataType || '').toLowerCase();
        if (fieldDataType === 'date' || fieldDataType === 'datetime' || fieldDataType === 'datetime2' || fieldDataType === 'smalldatetime') {
          const fieldName = field.fieldName;
          const value = updated[fieldName];
          
          if (value && value !== null && value !== undefined && value !== '') {
            // Check if it's already formatted (YYYY-MM-DD format)
            const isFormatted = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value);
            
            if (!isFormatted) {
              try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                  if (fieldDataType === 'date') {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    updated[fieldName] = `${year}-${month}-${day}`;
                    changed = true;
                  } else {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    const hours = String(date.getHours()).padStart(2, '0');
                    const minutes = String(date.getMinutes()).padStart(2, '0');
                    updated[fieldName] = `${year}-${month}-${day}T${hours}:${minutes}`;
                    changed = true;
                  }
                }
              } catch (e) {
                // Error formatting date field
              }
            }
          }
        }
      }
      
      if (changed) {
        setFormData(updated);
      }
    }
  }, [formData, fields, isEditMode]);

  const checkPermissions = async () => {
    try {
      const sessionRes = await authAPI.checkSession();
      if (sessionRes.data.success && sessionRes.data.user) {
        const userData = sessionRes.data.user;
        const userRole = userData.Role || userData.role || '';
        const isAdminUser = userRole.toLowerCase() === 'admin';
        setIsAdmin(isAdminUser);

        if (isAdminUser) {
          setPermissions({
            CanView: true,
            CanCreate: true,
            CanEdit: true
          });
          return;
        }

        const groupIds = userData.groupIds || [userData.groupId];
        const groupId = groupIds[0];

        if (groupId) {
          const permRes = await permissionsAPI.getGroupPermissions(groupId);
          if (permRes.data.success && permRes.data.tablePermissions) {
            const tablePerm = permRes.data.tablePermissions.find(
              tp => tp.TableName === tableName
            );
            if (tablePerm) {
              setPermissions({
                CanView: tablePerm.CanView === 1 || tablePerm.CanView === true,
                CanCreate: tablePerm.CanCreate === 1 || tablePerm.CanCreate === true,
                CanEdit: tablePerm.CanEdit === 1 || tablePerm.CanEdit === true
              });
            }
          }
        }
      }
    } catch (err) {
      // Error checking permissions
    }
  };

  const fetchTableStructure = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await tableAPI.getTableFields(tableName);
      if (response.data && response.data.fields) {
        const fetchedFields = response.data.fields;
        setFields(fetchedFields);

        const pkFields = fetchedFields
          .filter(f => f.isPrimaryKey)
          .map(f => f.fieldName);
        setPrimaryKeyFields(pkFields);

        const fks = fetchedFields
          .filter(f => f.foreignKey)
          .map(f => ({
            localColumn: f.fieldName,
            foreignTable: f.foreignKey.foreignTable,
            foreignColumn: f.foreignKey.foreignColumn
          }));
        setForeignKeys(fks);

        // Fetch field links separately from API
        try {
          const linksRes = await tableAPI.getFieldLinks(tableName);
          const links = linksRes.data.fieldLinks || linksRes.data || [];
          if (linksRes.data.success && links.length > 0) {
            // Parse SQLSource to extract LinkedTableName and LinkedFieldName
            const processedLinks = links.map(link => {
              let linkedTableName = null;
              let linkedFieldName = link.SQLDisplay || 'Name';
              
              if (link.SQLSource) {
                if (link.SQLSource.includes('[')) {
                  const match = link.SQLSource.match(/FROM\s+\[([^\]]+)\]/i);
                  if (match && match[1]) {
                    linkedTableName = match[1].trim();
                  }
                } else {
                  const match = link.SQLSource.match(/FROM\s+([a-zA-Z_][a-zA-Z0-9_]*)/i);
                  if (match && match[1]) {
                    linkedTableName = match[1].trim();
                  }
                }
              }
              
              return {
                FieldName: link.FieldName,
                IsLinked: link.IsLinked === 1 || link.IsLinked === true ? 1 : 0,
                LinkTable: linkedTableName,
                LinkField: linkedTableName ? (link.SQLDisplay || 'Name') : null,
                DisplayField: link.SQLDisplay || 'Name'
              };
            });
            setFieldLinks(processedLinks);
          } else {
            setFieldLinks([]);
          }
        } catch (err) {
          setFieldLinks([]);
        }

        await loadForeignKeys(fks);
        await loadCrossTableConfigs(fetchedFields);
        await fetchFormat();
        
        // Set fields state before loading record data so loadRecordData can use it
        setFields(fetchedFields);
        
        if (isEditMode) {
          // Pass fetchedFields directly to avoid state timing issues
          await loadRecordData(recordId, fetchedFields);
        } else {
          initializeFormData(fetchedFields);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load table structure');
    } finally {
      setLoading(false);
    }
  };

  const fetchFormat = async () => {
    try {
      const tableRes = await mgbSetupAPI.getTableByName(tableName);
      if (tableRes.data.success && tableRes.data.table) {
        const table = tableRes.data.table;
        const formatID = isEditMode ? table.EditFormatID : table.AddFormatID;
        
        if (formatID) {
          const formatRes = await mgbSetupAPI.getFormats();
          if (formatRes.data.success && formatRes.data.formats) {
            // Convert both to numbers for comparison to handle string/number mismatches
            const matchedFormat = formatRes.data.formats.find(f => Number(f.FormatID) === Number(formatID));
            if (matchedFormat) {
              setFormat(matchedFormat);
            }
          }
        }
      }
    } catch (err) {
      // Error fetching format
    }
  };

  const loadForeignKeys = async (fks) => {
    const fkData = {};
    for (const fk of fks) {
      try {
        const response = await tableAPI.getForeignKeyData(fk.foreignTable);
        if (response.data && response.data.data) {
          fkData[fk.localColumn] = response.data.data;
        }
      } catch (err) {
        // Error loading foreign key data
      }
    }
    setForeignKeyData(fkData);
  };

  const loadLinkedFieldOptions = async () => {
    const options = {};
    for (const link of fieldLinks) {
      if (link.IsLinked === 1 || link.IsLinked === true && link.LinkTable) {
        try {
          const response = await tableAPI.getLinkedFieldOptions(
            tableName,
            link.LinkTable,
            link.DisplayField
          );
          if (response.data && response.data.success && response.data.options) {
            options[link.FieldName] = response.data.options;
            // Also store under lowercase for case-insensitive lookup
            options[link.FieldName.toLowerCase()] = response.data.options;
          }
        } catch (err) {
          // Error loading linked field options
        }
      }
    }
    if (Object.keys(options).length > 0) {
      setLinkedFieldOptions(prev => ({ ...prev, ...options }));
    }
  };

  const loadDropdownFieldOptions = async () => {
    const options = {};
    for (const field of fields) {
      // Check if field is dropdown type with SQLSource (case-insensitive)
      const fieldType = (field.type || '').toLowerCase();
      const fieldNameLower = field.fieldName.toLowerCase();
      
      if (fieldType === 'dropdown' && field.SQLSource && field.SQLDisplay) {
        // Check if this field is already in fieldLinks (to avoid duplicate loading)
        const alreadyInFieldLinks = fieldLinks.some(
          link => link.FieldName && link.FieldName.toLowerCase() === fieldNameLower
        );
        
        if (!alreadyInFieldLinks) {
          // Extract table name from SQLSource
          let linkedTableName = null;
          if (field.SQLSource.includes('[')) {
            const match = field.SQLSource.match(/FROM\s+\[([^\]]+)\]/i);
            if (match && match[1]) {
              linkedTableName = match[1].trim();
            }
          } else {
            const match = field.SQLSource.match(/FROM\s+([a-zA-Z_][a-zA-Z0-9_]*)/i);
            if (match && match[1]) {
              linkedTableName = match[1].trim();
            }
          }
          
          if (linkedTableName) {
            try {
              const response = await tableAPI.getLinkedFieldOptions(
                tableName,
                linkedTableName,
                field.SQLDisplay
              );
              if (response.data && response.data.success && response.data.options) {
                options[field.fieldName] = response.data.options;
                // Also store under lowercase for case-insensitive lookup
                options[fieldNameLower] = response.data.options;
              }
            } catch (err) {
              // Error loading dropdown field options
            }
          }
        }
      }
    }
    if (Object.keys(options).length > 0) {
      setLinkedFieldOptions(prev => {
        // Merge with existing options, avoiding duplicates
        const merged = { ...prev };
        Object.keys(options).forEach(key => {
          if (!merged[key]) {
            merged[key] = options[key];
          }
        });
        return merged;
      });
    }
  };

  const loadCrossTableConfigs = async (fetchedFields) => {
    // Check for multiselect fields - handle case-insensitive type matching and various property name cases
    const multiSelectFields = fetchedFields.filter(f => {
      const fieldType = (f.type || f.Type || '').toLowerCase();
      // subRecordID can be 0 (falsy) if not set, so check for truthy AND > 0
      const subRecordID = f.subRecordID || f.SubRecordID || f.subrecordID;
      const hasSubRecordID = subRecordID && subRecordID > 0;
      const isMultiselect = fieldType === 'multiselect';
      return isMultiselect && hasSubRecordID;
    });
    
    const configs = {};
    const availableItems = {};
    
    for (const field of multiSelectFields) {
      try {
        const xbyxID = (field.subRecordID || field.SubRecordID || field.subrecordID);
        
        const res = await recordAPI.getCrossTableConfig(xbyxID);
        
        if (res.data && res.data.success && res.data.config) {
          configs[field.fieldName] = res.data.config;
          
          const itemsRes = await recordAPI.getCrossTableAvailableItems(xbyxID);
          if (itemsRes.data && itemsRes.data.success && itemsRes.data.items) {
            availableItems[field.fieldName] = {
              config: res.data.config,
              items: itemsRes.data.items
            };
          }
        }
      } catch (err) {
        // Error loading cross-table config
      }
    }
    
    setCrossTableConfigs(configs);
    setCrossTableAvailableItems(availableItems);
  };

  const initializeFormData = (fetchedFields) => {
    const initialFormData = {};
    fetchedFields.forEach(field => {
      const fieldDataType = field.datatype || field.dataType;
      const defaultValue = field.DefaultValue || field.defaultValue || '';
      
      if (fieldDataType === 'bit') {
        initialFormData[field.fieldName] = defaultValue && defaultValue.toString().includes('1') ? 1 : 0;
      } else if (fieldDataType === 'datetime' || fieldDataType === 'datetime2' || fieldDataType === 'smalldatetime' || fieldDataType === 'date' || fieldDataType === 'time') {
        const isGetDate = defaultValue && defaultValue.toString().toLowerCase().includes('getdate');
        if (isGetDate) {
          const now = new Date();
          if (fieldDataType === 'date') {
            initialFormData[field.fieldName] = now.toISOString().split('T')[0];
          } else if (fieldDataType === 'time') {
            initialFormData[field.fieldName] = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
          } else {
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            initialFormData[field.fieldName] = `${year}-${month}-${day}T${hours}:${minutes}`;
          }
        } else if (defaultValue && defaultValue !== '') {
          initialFormData[field.fieldName] = defaultValue;
        }
      } else if (defaultValue && defaultValue !== '') {
        initialFormData[field.fieldName] = defaultValue;
      }
    });
    setFormData(initialFormData);
  };

  const loadRecordData = async (id, fieldsToUse = null) => {
    try {
      // Use passed fields or fall back to state (for backwards compatibility)
      const fieldsForLoading = fieldsToUse || fields;
      
      const response = await recordAPI.getRecord(tableName, id);
      
      if (response.data && response.data.success && response.data.record) {
        let record = response.data.record;
        
        // IMMEDIATE FIX: Format HireDate right after getting the record, before any other processing
        if (record.HireDate !== null && record.HireDate !== undefined && record.HireDate !== '') {
          try {
            const date = new Date(record.HireDate);
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              record.HireDate = `${year}-${month}-${day}`;
            }
          } catch (e) {
            // Error formatting HireDate
          }
        }
        
        // Format date/datetime fields for HTML input compatibility
        for (const field of fieldsForLoading) {
          const fieldDataType = (field.datatype || field.dataType || '').toLowerCase();
          const fieldName = field.fieldName;
          
          if (fieldDataType === 'date' || fieldDataType === 'datetime' || fieldDataType === 'datetime2' || fieldDataType === 'smalldatetime' || fieldDataType === 'time') {
            // Try both exact field name and case-insensitive match
            let dateValue = record[fieldName];
            
            // If not found with exact match, try case-insensitive
            if (dateValue === undefined || dateValue === null) {
              const fieldNameLower = fieldName.toLowerCase();
              for (const key in record) {
                if (key.toLowerCase() === fieldNameLower) {
                  dateValue = record[key];
                  // Update the record to use the correct case
                  if (key !== fieldName) {
                    record[fieldName] = dateValue;
                    delete record[key];
                  }
                  break;
                }
              }
            }
            
            if (dateValue !== null && dateValue !== undefined && dateValue !== '') {
              try {
                // Handle different date formats from database
                let date;
                if (dateValue instanceof Date) {
                  date = dateValue;
                } else if (typeof dateValue === 'string') {
                  // Try parsing the string date - handle various formats
                  // SQL Server dates come as "2004-05-01T00:00:00.000Z" (ISO format)
                  const dateStr = dateValue.trim();
                  // Directly parse ISO string - Date constructor handles it
                  date = new Date(dateStr);
                } else if (typeof dateValue === 'number') {
                  // Handle timestamp
                  date = new Date(dateValue);
                } else {
                  // Try to convert to date (handles Date objects serialized through JSON)
                  date = new Date(dateValue);
                }
                
                if (!isNaN(date.getTime()) && date instanceof Date) {
                  if (fieldDataType === 'date') {
                    // Format as YYYY-MM-DD for date input
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    record[fieldName] = `${year}-${month}-${day}`;
                  } else if (fieldDataType === 'time') {
                    // Format as HH:mm for time input
                    const hours = String(date.getHours()).padStart(2, '0');
                    const minutes = String(date.getMinutes()).padStart(2, '0');
                    record[fieldName] = `${hours}:${minutes}`;
                  } else {
                    // Format as YYYY-MM-DDTHH:mm for datetime-local input
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    const hours = String(date.getHours()).padStart(2, '0');
                    const minutes = String(date.getMinutes()).padStart(2, '0');
                    record[fieldName] = `${year}-${month}-${day}T${hours}:${minutes}`;
                  }
                }
              } catch (e) {
                // If date parsing fails, leave the value as is
              }
            }
          }
          
          // Clear password fields
          if ((field.type || '').toLowerCase() === 'password') {
            record[field.fieldName] = '';
          }
        }
        
        // Load cross-table selected items
        const multiSelectFields = fieldsForLoading.filter(f => {
          const fieldType = (f.type || f.Type || '').toLowerCase();
          const subRecordID = f.subRecordID || f.SubRecordID || f.subrecordID;
          const hasSubRecordID = subRecordID && subRecordID > 0;
          const isMultiselect = fieldType === 'multiselect';
          return isMultiselect && hasSubRecordID;
        });
        const selectedItems = {};
        
        for (const field of multiSelectFields) {
          try {
            const xbyxID = field.subRecordID || field.SubRecordID || field.subrecordID;
            const res = await recordAPI.getCrossTableSelectedItems(xbyxID, id, tableName);
            if (res.data && res.data.success) {
              const validItems = (res.data.selected || []).filter(item => item !== null && item !== undefined);
              selectedItems[field.fieldName] = validItems;
            }
          } catch (err) {
            // Error loading cross-table items
          }
        }
        
        setCrossTableSelectedItems(selectedItems);
        
        // CRITICAL FIX: Format HireDate directly - this runs regardless of field detection
        // Check all possible date field names (case-insensitive)
        for (const key in record) {
          if (key.toLowerCase() === 'hiredate' && record[key] !== null && record[key] !== undefined && record[key] !== '') {
            try {
              const dateValue = record[key];
              // Check if already in YYYY-MM-DD format
              if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateValue))) {
                const date = new Date(dateValue);
                if (!isNaN(date.getTime())) {
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  record[key] = `${year}-${month}-${day}`;
                  // Also set with correct case
                  if (key !== 'HireDate') {
                    record.HireDate = record[key];
                  }
                }
              }
            } catch (e) {
              // Error formatting HireDate
            }
          }
        }
        
        // Set form data - linked field conversion will happen in useEffect when linkedFieldOptions are ready
        // Force format HireDate one more time before setting
        if (record.HireDate && typeof record.HireDate === 'string' && !/^\d{4}-\d{2}-\d{2}$/.test(record.HireDate)) {
          try {
            const date = new Date(record.HireDate);
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              record.HireDate = `${year}-${month}-${day}`;
            }
          } catch (e) {
            // Error formatting HireDate
          }
        }
        
        setFormData(record);
      } else if (response.data && !response.data.success) {
        setError(response.data.message || 'Record not found');
      } else {
        setError('Record not found');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load record';
      setError(errorMessage);
    }
  };

  const getFieldDisplayName = (fieldName) => {
    const field = fields.find(f => f.fieldName === fieldName);
    return field?.displayName || fieldName;
  };

  const renderFormField = (field) => {
    const linkedField = fieldLinks.find(fl => 
      fl.FieldName && fl.FieldName.toLowerCase() === field.fieldName.toLowerCase()
    );
    const isLinked = linkedField && (linkedField.IsLinked === 1 || linkedField.IsLinked === true);
    
    // Check if field is dropdown type (either in fieldLinks or has SQLSource directly)
    const fieldType = (field.type || '').toLowerCase();
    const isDropdownField = fieldType === 'dropdown' && field.SQLSource;
    const fieldNameLower = field.fieldName.toLowerCase();
    const hasDropdownOptions = linkedFieldOptions[field.fieldName] || linkedFieldOptions[fieldNameLower];
    
    const fieldDataType = (field.datatype || field.dataType || '').toLowerCase();
    
    let fieldInputType = 'text';
    if (fieldDataType === 'int' || fieldDataType === 'bigint' || fieldDataType === 'smallint' || fieldDataType === 'tinyint') {
      fieldInputType = 'number';
    } else if (fieldDataType === 'datetime' || fieldDataType === 'datetime2' || fieldDataType === 'smalldatetime') {
      fieldInputType = 'datetime-local';
    } else if (fieldDataType === 'date') {
      fieldInputType = 'date';
    } else if (fieldDataType === 'time') {
      fieldInputType = 'time';
    } else if ((field.type || '').toLowerCase() === 'password') {
      fieldInputType = 'password';
    } else if (fieldDataType === 'decimal' || fieldDataType === 'numeric' || fieldDataType === 'float' || fieldDataType === 'real' || fieldDataType === 'money' || fieldDataType === 'smallmoney') {
      fieldInputType = 'number';
    } else if (fieldDataType === 'bit') {
      fieldInputType = 'checkbox';
    }
    
    const parseNumeric = (value) => {
      if (value === null || value === undefined) return null;
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined' || trimmed.toLowerCase() === 'null') return null;
        const parsed = parseFloat(trimmed);
        return !isNaN(parsed) && isFinite(parsed) ? parsed : null;
      }
      return null;
    };
    
    const displayLength = parseNumeric(field.displayLength || field.DisplayLength);
    const maxLength = parseNumeric(field.maxLength || field.MaxLength) || parseNumeric(field.schemaMaxLength);
    const height = parseNumeric(field.height || field.Height);
    const width = parseNumeric(field.width || field.Width);
    
    const fieldStyle = {};
    if (width && typeof width === 'number') fieldStyle.width = `${width}px`;
    if (height && typeof height === 'number') fieldStyle.height = `${height}px`;
    if (!width && displayLength && typeof displayLength === 'number' && displayLength > 0) {
      fieldStyle.width = `${displayLength * 0.6}em`;
      fieldStyle.minWidth = `${displayLength * 0.5}em`;
    }
    
    const defaultValue = field.DefaultValue || field.defaultValue || '';
    const isGetDate = defaultValue && defaultValue.toString().toLowerCase().includes('getdate');
    const shouldSetCurrentDateTime = isGetDate && (fieldDataType === 'datetime' || fieldDataType === 'datetime2' || fieldDataType === 'smalldatetime' || fieldDataType === 'date' || fieldDataType === 'time');
    
    let initialDateValue = formData[field.fieldName];
    
    // Date field rendering
    
    if (shouldSetCurrentDateTime && !isEditMode && (!formData[field.fieldName] || formData[field.fieldName] === '')) {
      const now = new Date();
      if (fieldDataType === 'date') {
        initialDateValue = now.toISOString().split('T')[0];
      } else if (fieldDataType === 'time') {
        initialDateValue = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      } else {
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        initialDateValue = `${year}-${month}-${day}T${hours}:${minutes}`;
      }
      if (!formData[field.fieldName] || formData[field.fieldName] === '') {
        setFormData(prev => ({
          ...prev,
          [field.fieldName]: initialDateValue
        }));
      }
    }

    return (
      <Form.Group className="mb-3">
        <Form.Label>{getFieldDisplayName(field.fieldName)}</Form.Label>
        
        {(field.type || '').toLowerCase() === 'multiselect' ? (
          (() => {
            const subRecordID = field.subRecordID || field.SubRecordID || field.subrecordID;
            const hasSubRecordID = subRecordID && subRecordID > 0;
            
            if (!hasSubRecordID) {
              return (
                <div className="alert alert-info" role="alert">
                  <strong>Configuration Required:</strong> This multiselect field needs to be configured with a cross-table relationship. 
                  Please set the <code>subRecordID</code> in the field configuration to link it to a cross-table configuration.
                </div>
              );
            }
            
            if (crossTableAvailableItems[field.fieldName]) {
              return (
                <div className="border p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                  {crossTableAvailableItems[field.fieldName].items.map((item) => {
                    const config = crossTableAvailableItems[field.fieldName].config;
                    let primaryKeyName, displayName;
                    if (config.RightTable && tableName.toLowerCase() === config.RightTable.toLowerCase()) {
                      primaryKeyName = config.LeftPrimary || config.leftPrimary;
                      const tableBaseName = config.LeftTable.replace(/^MGB_/, '');
                      const singularBase = tableBaseName.replace(/s$/, '');
                      displayName = `${singularBase}Name`;
                    } else {
                      primaryKeyName = config.RightPrimary || config.rightPrimary;
                      displayName = config.RightName || config.rightName;
                    }
                    const itemID = item[primaryKeyName];
                    const itemName = item[displayName];
                    const selectedItemsForField = (crossTableSelectedItems[field.fieldName] || []).map(s => String(s));
                    const isSelected = selectedItemsForField.includes(String(itemID));
                    
                    return (
                      <Form.Check
                        key={`${field.fieldName}-${itemID}`}
                        type="checkbox"
                        label={itemName}
                        checked={isSelected}
                        onChange={(e) => {
                          const newSelected = e.target.checked
                            ? [...(crossTableSelectedItems[field.fieldName] || []), itemID]
                            : (crossTableSelectedItems[field.fieldName] || []).filter(id => String(id) !== String(itemID));
                          setCrossTableSelectedItems({
                            ...crossTableSelectedItems,
                            [field.fieldName]: newSelected
                          });
                        }}
                        className="mb-2"
                      />
                    );
                  })}
                </div>
              );
            } else {
              return (
                <div className="alert alert-warning" role="alert">Loading options...</div>
              );
            }
          })()
        ) : (isDropdownField && hasDropdownOptions) || (!field.type.includes('multiselect') && isLinked && hasDropdownOptions) ? (
          <Form.Select
            value={formData[field.fieldName] !== undefined && formData[field.fieldName] !== null ? String(formData[field.fieldName]) : ''}
            onChange={(e) => setFormData({ ...formData, [field.fieldName]: e.target.value })}
          >
            <option value="">Select {getFieldDisplayName(field.fieldName)}</option>
            {(linkedFieldOptions[field.fieldName] || linkedFieldOptions[field.fieldName.toLowerCase()] || []).map(item => {
              const optionValue = tableName === 'MGB_TablePermissions' && field.fieldName === 'TableName' 
                ? item.display 
                : String(item.id);
              return (
                <option key={item.id} value={optionValue}>{item.display}</option>
              );
            })}
          </Form.Select>
        ) : fieldDataType === 'bit' ? (
          <div className="d-flex gap-3">
            <Form.Check
              type="radio"
              label="Yes"
              name={field.fieldName}
              value="1"
              checked={formData[field.fieldName] === 1 || formData[field.fieldName] === '1' || formData[field.fieldName] === true}
              onChange={(e) => setFormData({ ...formData, [field.fieldName]: 1 })}
            />
            <Form.Check
              type="radio"
              label="No"
              name={field.fieldName}
              value="0"
              checked={formData[field.fieldName] === 0 || formData[field.fieldName] === '0' || formData[field.fieldName] === false}
              onChange={(e) => setFormData({ ...formData, [field.fieldName]: 0 })}
            />
          </div>
        ) : foreignKeys.some(fk => fk.localColumn === field.fieldName) ? (
          <Form.Select
            value={formData[field.fieldName] !== undefined && formData[field.fieldName] !== null ? String(formData[field.fieldName]) : ''}
            onChange={(e) => setFormData({ ...formData, [field.fieldName]: e.target.value })}
          >
            <option value="">Select {getFieldDisplayName(field.fieldName)}</option>
            {(foreignKeyData[field.fieldName] || []).map(item => {
              const optionValue = tableName === 'MGB_TablePermissions' && field.fieldName === 'TableName' 
                ? item.display 
                : String(item.id);
              return (
                <option key={item.id} value={optionValue}>{item.display}</option>
              );
            })}
          </Form.Select>
        ) : (
          <>
            {height && height > 50 ? (
              <Form.Control
                as="textarea"
                rows={Math.ceil(height / 20) || 3}
                placeholder={`Enter ${getFieldDisplayName(field.fieldName)}`}
                value={formData[field.fieldName] !== undefined && formData[field.fieldName] !== null ? String(formData[field.fieldName]) : ''}
                onChange={(e) => setFormData({ ...formData, [field.fieldName]: e.target.value })}
                required={(((field.type || '').toLowerCase() === 'password' || field.fieldName === 'UserPassword') && isEditMode) ? false : (field.isNullable === 'NO')}
                maxLength={maxLength && maxLength > 0 ? Math.floor(maxLength) : undefined}
                style={Object.keys(fieldStyle).length > 0 ? fieldStyle : (width || height ? { width: width ? `${width}px` : undefined, height: height ? `${height}px` : undefined } : undefined)}
              />
            ) : (
              <Form.Control
                type={fieldInputType}
                placeholder={`Enter ${getFieldDisplayName(field.fieldName)}`}
                value={(() => {
                  // For date/datetime fields, prioritize formData value
                  if (fieldInputType === 'date' || fieldInputType === 'datetime-local' || fieldInputType === 'time') {
                    let formValue = formData[field.fieldName];
                    
                    // Special handling for HireDate - try multiple ways to get the value
                    if (field.fieldName === 'HireDate' && (!formValue || formValue === '')) {
                      // Try case-insensitive lookup
                      for (const key in formData) {
                        if (key.toLowerCase() === 'hiredate') {
                          formValue = formData[key];
                          break;
                        }
                      }
                    }
                    
                    // Use formData value if it exists and is not empty
                    if (formValue !== undefined && formValue !== null && formValue !== '') {
                      let strValue = String(formValue).trim();
                      
                      // Ensure it's in the right format for date input (YYYY-MM-DD)
                      if (fieldInputType === 'date') {
                        // If not in YYYY-MM-DD format, try to parse and reformat
                        if (!/^\d{4}-\d{2}-\d{2}$/.test(strValue)) {
                          try {
                            const date = new Date(strValue);
                            if (!isNaN(date.getTime())) {
                              const year = date.getFullYear();
                              const month = String(date.getMonth() + 1).padStart(2, '0');
                              const day = String(date.getDate()).padStart(2, '0');
                              strValue = `${year}-${month}-${day}`;
                            }
                          } catch (e) {
                            // Error reformatting date field
                          }
                        }
                      }
                      return strValue;
                    }
                    
                    // Fallback to initialDateValue for new records
                    if (initialDateValue !== undefined && initialDateValue !== '') {
                      return String(initialDateValue);
                    }
                    
                    return '';
                  }
                  
                  // For non-date fields, use standard logic
                  return initialDateValue !== undefined ? initialDateValue : (formData[field.fieldName] !== undefined && formData[field.fieldName] !== null ? String(formData[field.fieldName]) : '');
                })()}
                onChange={(e) => setFormData({ ...formData, [field.fieldName]: e.target.value })}
                required={(((field.type || '').toLowerCase() === 'password' || field.fieldName === 'UserPassword') && isEditMode) ? false : (field.isNullable === 'NO')}
                maxLength={maxLength && maxLength > 0 ? Math.floor(maxLength) : undefined}
                size={displayLength && displayLength > 0 ? Math.floor(displayLength) : undefined}
                style={Object.keys(fieldStyle).length > 0 ? fieldStyle : undefined}
                {...(fieldInputType === 'number' && fieldDataType !== 'int' && fieldDataType !== 'bigint' ? { step: 'any' } : {})}
              />
            )}
            {((field.type || '').toLowerCase() === 'password') && (
              <Form.Control
                type="password"
                placeholder={`Confirm ${getFieldDisplayName(field.fieldName)}`}
                value={formData[`${field.fieldName}_confirm`] !== undefined && formData[`${field.fieldName}_confirm`] !== null ? String(formData[`${field.fieldName}_confirm`]) : ''}
                onChange={(e) => setFormData({ ...formData, [`${field.fieldName}_confirm`]: e.target.value })}
                className="mt-2"
                required={(((field.type || '').toLowerCase() === 'password' || field.fieldName === 'UserPassword') && isEditMode) ? false : (field.isNullable === 'NO')}
              />
            )}
          </>
        )}
      </Form.Group>
    );
  };

  const handleSave = async () => {
    try {
      // Validate required fields
      for (const field of fields) {
        if (field.isNullable === 'NO' && primaryKeyFields.includes(field.fieldName)) {
          continue;
        }
        if (field.isNullable === 'NO' && !field.addable && !isEditMode) {
          continue;
        }
        // Password is optional when editing (only update if user enters a new one)
        const isPasswordField = (field.type || '').toLowerCase() === 'password' || field.fieldName === 'UserPassword';
        if (isPasswordField && isEditMode) {
          continue;
        }
        if (field.isNullable === 'NO') {
          const value = formData[field.fieldName];
          if (value === undefined || value === null || value === '') {
            setError(`${getFieldDisplayName(field.fieldName)} is required`);
            return;
          }
        }
      }
      
      const dataToSave = { ...formData };
      
      // Remove multiselect fields from dataToSave
      for (const field of fields) {
        if ((field.type || '').toLowerCase() === 'multiselect') {
          delete dataToSave[field.fieldName];
        }
      }
      
      // Special handling for MGB_TablePermissions
      if (tableName === 'MGB_TablePermissions' && dataToSave.TableName && foreignKeyData['TableName']) {
        const selectedTableName = String(dataToSave.TableName);
        if (isNaN(selectedTableName)) {
          const matchedTable = foreignKeyData['TableName'].find(
            item => String(item.display) === selectedTableName
          );
          if (matchedTable) {
            dataToSave.TableName = matchedTable.id;
          }
        }
      }
      
      // Validate password fields
      for (const field of fields) {
        const isPasswordField = (field.type || '').toLowerCase() === 'password' || field.fieldName === 'UserPassword';
        if (isPasswordField) {
          const password = dataToSave[field.fieldName];
          const confirmPassword = dataToSave[`${field.fieldName}_confirm`];
          const isEncryptedHash = password && typeof password === 'string' && password.startsWith('$2a$');
          
          if (!isEditMode && !password) {
            setError(`${getFieldDisplayName(field.fieldName)} is required`);
            return;
          }
          
          if (password && !isEncryptedHash && password !== confirmPassword) {
            setError(`${getFieldDisplayName(field.fieldName)} and confirmation do not match`);
            return;
          }
          
          if (isEditMode && (!password || isEncryptedHash)) {
            delete dataToSave[field.fieldName];
          }
          
          delete dataToSave[`${field.fieldName}_confirm`];
        }
      }
      
      // Check for duplicate records before saving
      try {
        const duplicateCheck = await tableAPI.checkDuplicate(tableName, dataToSave, isEditMode ? recordId : null);
        if (duplicateCheck.data && duplicateCheck.data.isDuplicate) {
          setError(duplicateCheck.data.message || 'A duplicate record already exists.');
          return;
        }
      } catch (dupErr) {
        // If duplicate check fails, log but don't block the save
        // (in case the endpoint doesn't exist or there's a network issue)
      }
      
      if (isEditMode) {
        await saveCrossTableRelationships(recordId);
        await recordAPI.updateRecord(tableName, recordId, dataToSave);
      } else {
        const result = await recordAPI.createRecord(tableName, dataToSave);
        const newRecordID = result.data?.id;
        if (newRecordID) {
          await saveCrossTableRelationships(newRecordID);
        }
      }
      
      // Navigate back to listing page
      navigate(`/tables/${tableName}`);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save record';
      setError(errorMessage);
    }
  };

  const saveCrossTableRelationships = async (recordID) => {
    const multiselectFields = fields.filter(f => {
      const t = (f.type || f.Type || '').toLowerCase();
      const sub = f.subRecordID ?? f.SubRecordID ?? f.subrecordID;
      return t === 'multiselect' && sub && Number(sub) > 0;
    });
    for (const field of multiselectFields) {
      const xbyxID = Number(field.subRecordID ?? field.SubRecordID ?? field.subrecordID);
      const key = field.fieldName;
      const selectedIDs = crossTableSelectedItems[key];
      const idsToSend = Array.isArray(selectedIDs) ? selectedIDs.map(id => Number(id)).filter(n => !isNaN(n)) : [];
      try {
        await recordAPI.saveCrossTableRelationships(
          xbyxID,
          recordID,
          idsToSend,
          tableName
        );
      } catch (ctErr) {
        setError(ctErr.response?.data?.message || ctErr.message || 'Failed to save group selection');
        throw ctErr;
      }
    }
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div>Loading...</div>
      </Container>
    );
  }

  if (!isEditMode && !permissions.CanCreate) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">You do not have permission to create records.</Alert>
        <Button variant="secondary" onClick={() => navigate(`/tables/${tableName}`)}>
          <FiArrowLeft className="me-2" /> Back
        </Button>
      </Container>
    );
  }

  if (isEditMode && !permissions.CanEdit) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">You do not have permission to edit records.</Alert>
        <Button variant="secondary" onClick={() => navigate(`/tables/${tableName}`)}>
          <FiArrowLeft className="me-2" /> Back
        </Button>
      </Container>
    );
  }

  // Filter fields to render
  const fieldsToRender = fields.filter(field => {
    if (primaryKeyFields.includes(field.fieldName)) return false;
    const canShowField = isEditMode ? field.editable : field.addable;
    return canShowField;
  });

  // Get format config
  let formatConfig = null;
  try {
    if (format && format.FormatConfig) {
      formatConfig = typeof format.FormatConfig === 'string' 
        ? JSON.parse(format.FormatConfig) 
        : format.FormatConfig;
    }
  } catch (e) {
    // Format config parsing failed
  }
  
  const layoutType = format ? format.LayoutType : 'SingleColumn';

  // Get table display name
  const getTableDisplayName = () => {
    if (format && format.HeaderLabel) {
      return format.HeaderLabel;
    }
    // Remove MGB_ prefix and format nicely
    return tableName.replace(/^MGB_/, '').replace(/_/g, ' ');
  };

  return (
    <Container className="mt-4">
      <div className="mb-4">
        <h1 className="mb-2 text-center">{getTableDisplayName()}</h1>
        <div className="d-flex justify-content-between align-items-center position-relative">
          <div style={{ width: '120px' }}></div>
          <strong className="mb-0 text-center flex-grow-1" style={{ fontSize: '1.5rem' }}>{isEditMode ? 'Edit Record' : 'Add Record'}</strong>
          <div className="d-flex justify-content-end" style={{ width: '120px' }}>
            <Button variant="secondary" onClick={() => navigate(`/tables/${tableName}`)} style={{ width: '120px' }}>
              <FiArrowLeft className="me-2" /> Back
            </Button>
          </div>
        </div>
      </div>

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      <Form>
        {layoutType === 'TwoColumn' || (formatConfig && formatConfig.fieldsPerRow === 2) ? (
          <Row>
            {fieldsToRender.map((field, index) => (
              <Col md={6} key={field.fieldName}>
                {renderFormField(field)}
              </Col>
            ))}
          </Row>
        ) : layoutType === 'ThreeColumn' || (formatConfig && formatConfig.fieldsPerRow === 3) ? (
          <Row>
            {fieldsToRender.map((field, index) => (
              <Col md={4} key={field.fieldName}>
                {renderFormField(field)}
              </Col>
            ))}
          </Row>
        ) : (
          fieldsToRender.map(field => (
            <div key={field.fieldName}>
              {renderFormField(field)}
            </div>
          ))
        )}
      </Form>

      <div className="mt-4 d-flex gap-2">
        <Button variant="primary" onClick={handleSave}>
          <FiSave className="me-2" /> {isEditMode ? 'Update' : 'Create'}
        </Button>
        <Button variant="secondary" onClick={() => navigate(`/tables/${tableName}`)}>
          Cancel
        </Button>
      </div>
    </Container>
  );
}

export default TableForm;
